// frontend/src/hooks/useBilling.ts
import { useState, useEffect } from 'react'
import { Capacitor, registerPlugin } from '@capacitor/core'
import axios from 'axios'
import { PreviewOptions } from '../types/preview'

interface BillingPlugin {
  initialize(): Promise<void>
  queryProducts(options: { productIds: string[] }): Promise<{ products: BillingProduct[] }>
  purchase(options: { productId: string }): Promise<{ purchaseToken: string; orderId: string; productId: string }>
  acknowledgePurchase(options: { purchaseToken: string }): Promise<void>
}

export interface BillingProduct {
  id: string
  title: string
  price: string
  priceAmountMicros: number
}

const BillingPlugin = registerPlugin<BillingPlugin>('Billing')

const PRODUCT_IDS = ['premium_pdf_export', 'premium_plus_media']
const API_BASE = 'https://chatarchiv.de'

export function useBilling() {
  const [products, setProducts] = useState<BillingProduct[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    BillingPlugin.initialize()
      .then(() => BillingPlugin.queryProducts({ productIds: PRODUCT_IDS }))
      .then(({ products }) => setProducts(products))
      .catch(console.error)
  }, [])

  const purchase = async (
    productId: string,
    jobId: string,
    previewOptions: PreviewOptions
  ): Promise<string> => {
    setLoading(true)
    try {
      const { purchaseToken } = await BillingPlugin.purchase({ productId })

      let renderJobId: string
      try {
        const { data } = await axios.post(`${API_BASE}/api/payments/verify-google-play`, {
          purchase_token: purchaseToken,
          product_id: productId,
          job_id: jobId,
          preview_options: {
            ...previewOptions,
            dateRange: {
              from: previewOptions.dateRange.from?.toISOString() ?? null,
              to: previewOptions.dateRange.to?.toISOString() ?? null,
            },
          },
          consent: {
            agb_accepted: true,
            widerruf_accepted: true,
            agb_version: '2026-04',
            accepted_at: new Date().toISOString(),
          },
        })
        renderJobId = data.render_job_id
      } catch (backendError) {
        // Backend-Verifikation fehlgeschlagen — Kauf NICHT bestätigen.
        // Google Play erstattet automatisch nach 3 Tagen bei fehlender Bestätigung.
        throw backendError
      }

      // Nur bestätigen wenn Backend-Verifikation erfolgreich war
      await BillingPlugin.acknowledgePurchase({ purchaseToken })
      return renderJobId
    } finally {
      setLoading(false)
    }
  }

  return { products, loading, purchase }
}
